// ============================================================
// VB-029 — Entity Relations Service
//
// Minimal service for persisting semantic links between entities
// as validated by the user in the Entity Preview (AI-023).
//
// Write-only in V1: relations are persisted to Supabase but not
// yet read back into the app (no UI listing them yet).
// ============================================================

import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import { showToast } from '../utils/toast';

type DbRelationInsert = Database['public']['Tables']['entity_relations']['Insert'];
export type DbRelationRow = Database['public']['Tables']['entity_relations']['Row'];

export const entityRelationsService = {
  /**
   * VB-030 — Fetch all semantic relations involving a given entity (source OR target).
   * Used by EntityModal to display persisted links in the Relations sidebar.
   */
  async getRelationsForEntity(entityId: string): Promise<DbRelationRow[]> {
    const { data, error } = await supabase
      .from('entity_relations')
      .select('*')
      .or(`source_id.eq.${entityId},target_id.eq.${entityId}`)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[entity-relations.service] getRelationsForEntity:', error.message);
      return [];
    }
    return data ?? [];
  },

  /**
   * Persist a single semantic relation between two entities.
   * Both source and target must be valid UUIDs (existing Supabase row IDs).
   * Types should use the store vocabulary (e.g. 'task', 'goal', 'note', 'event', 'contact').
   *
   * VB-031 — Deduplication: if an identical relation already exists for this user
   * (same source_type, source_id, target_type, target_id, relation_type), the insert
   * is silently skipped to prevent duplicate rows.
   * A UNIQUE constraint at DB level (schema_updates_entity_relations_dedup.sql) adds a
   * second safety net — apply it manually in Supabase cloud for full protection.
   */
  async createRelation(
    sourceType: string,
    sourceId: string,
    targetType: string,
    targetId: string,
    relationType: string
  ): Promise<void> {
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();
    if (authError) throw authError;
    if (!user) throw new Error('Vous devez être connecté.');

    // VB-031 — Application-level deduplication check
    // Using limit(1) instead of maybeSingle() so the check stays robust
    // even if historical duplicates already exist in the table.
    const { data: existing, error: checkError } = await supabase
      .from('entity_relations')
      .select('id')
      .eq('user_id', user.id)
      .eq('source_type', sourceType)
      .eq('source_id', sourceId)
      .eq('target_type', targetType)
      .eq('target_id', targetId)
      .eq('relation_type', relationType)
      .limit(1);

    if (checkError) {
      console.error('[entity-relations.service] createRelation (check):', checkError.message);
      throw checkError;
    }
    if (existing && existing.length > 0) return; // relation already exists — skip silently

    const insertData: DbRelationInsert = {
      user_id: user.id,
      source_type: sourceType,
      source_id: sourceId,
      target_type: targetType,
      target_id: targetId,
      relation_type: relationType,
    };

    const { error } = await supabase.from('entity_relations').insert(insertData);

    if (error) {
      console.error('[entity-relations.service] createRelation (insert):', error.message);
      showToast('Impossible de créer le lien entre éléments', 'error');
      throw error;
    }
  },
};
